import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, orderBy, Timestamp } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Receipt,
  Wrench,
  ArrowRight,
  TrendingUp,
  Calendar,
  Loader2,
} from "lucide-react";

const Dashboard = () => {
  const [stats, setStats] = useState({ invoices: null, quotations: null, serviceBills: null });
  const [statsLoading, setStatsLoading] = useState(true);

  const quickActions = [
    { label: "New Quotation", icon: FileText, path: "/quotation", color: "bg-primary hover:bg-primary/90", description: "Create quotation for vehicle sale" },
    { label: "New Invoice", icon: Receipt, path: "/invoice", color: "bg-accent hover:bg-accent/90", description: "Generate tax invoice / bill" },
    { label: "Service Bill", icon: Wrench, path: "/service-bill", color: "bg-warning hover:bg-warning/90", description: "Create service / repair bill" },
  ];

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        // Get start of current month
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startTimestamp = Timestamp.fromDate(startOfMonth);

        const [invSnap, quotSnap, svcSnap] = await Promise.all([
          getDocs(query(collection(db, "invoices"), where("savedAt", ">=", startTimestamp))),
          getDocs(query(collection(db, "quotations"), where("savedAt", ">=", startTimestamp))),
          getDocs(query(collection(db, "service_bills"), where("savedAt", ">=", startTimestamp))),
        ]);

        setStats({
          invoices: invSnap.size,
          quotations: quotSnap.size,
          serviceBills: svcSnap.size,
        });
      } catch (err) {
        // Silently fail — stats are informational only
        setStats({ invoices: "—", quotations: "—", serviceBills: "—" });
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    { label: "Invoices This Month", value: stats.invoices, icon: Receipt, color: "text-primary" },
    { label: "Quotations This Month", value: stats.quotations, icon: FileText, color: "text-accent" },
    { label: "Service Bills This Month", value: stats.serviceBills, icon: Wrench, color: "text-warning" },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-xl p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-foreground">
              Welcome to SHREE MOBILITY TRADE
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Authorized Kinetic Green Dealership - Create and print quotations, invoices, and service bills for E-Rickshaws and Totos.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              NH 14, Brahmanigram more, Rampurhat | Mob: 9749430705 / 7063511705
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label} className="border-border">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className={`text-3xl font-bold mt-1 ${stat.color}`}>
                    {statsLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin inline" />
                    ) : (
                      stat.value ?? "—"
                    )}
                  </p>
                </div>
                <div className={`p-3 rounded-lg bg-secondary`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Create New Document</CardTitle>
          <CardDescription>Select document type to create and print</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <Link key={index} to={action.path} className="block">
                <Card className="border-border hover:shadow-md transition-all duration-200 hover:border-primary/30 h-full">
                  <CardContent className="pt-6 flex flex-col h-full">
                    <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center mb-4`}>
                      <action.icon className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">{action.label}</h3>
                    <p className="text-sm text-muted-foreground mb-4 flex-1">{action.description}</p>
                    <Button className={`${action.color} text-primary-foreground gap-2 w-full mt-auto`}>
                      Create
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Vehicle Models Available</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Kinetic Safar Smart, Jumbo, Star, Shakti, E-Luna, Zing HSS and more 3-wheeler electric vehicles.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-gradient-to-br from-accent/5 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-accent/10">
                <Wrench className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Service &amp; Maintenance</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate detailed service bills with parts, labor, and GST breakdown for all repairs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
